import type { FC } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useDispatch } from "react-redux";

import type { BacklinksTimeSeriesNewLostSummary } from "@similarweb/pro-api-client";
import { ColumnChartGraph } from "@similarweb/pro-logic-components";
import { TabList, Tabs } from "@similarweb/ui-components";

import { showErrorToast } from "app/actions/toast_actions";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import {
    BacklinksChartBoxContainer,
    BacklinksChartContainer,
    StyledTab,
} from "app/pages/digital-marketing/backlinks/reports/overview/BacklinksOverviewStyledComponents";
import { useBacklinksTimeseriesNewLostApi } from "app/pages/digital-marketing/backlinks/shared/backlinks-api-service";
import {
    API_DATE_REQUEST_FORMAT,
    BacklinksChartColors,
    chartTimeGranularityOptions,
    chartTimeRangeOptions,
    GRANULARITY_INDEX,
    NEW_VS_LOST_GRAPH_TABS,
    START_DATE_13_M,
    TIME_RANGES_INDEX,
    TODAY,
} from "app/pages/digital-marketing/backlinks/shared/BacklinksConsts";
import {
    currentGranularityAllowedInRange,
    filterAggregateDataByGranularity,
    filterDataFromDate,
    getExcelDownloadURL,
    getGranularityOptionsWithState,
    useExpectedBacklinksParams,
} from "app/pages/digital-marketing/backlinks/shared/BacklinksUtils";
import { allTrackers } from "app/services/track/track";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import dayjs from "single-spa/dayjs-extend";

import { getNewLostChartConfig } from "./BacklinksNewLostChartUtils";

export const BacklinksNewLostCharts: FC = () => {
    const params = useExpectedBacklinksParams();
    const translate = useTranslation();
    const dispatch = useDispatch();
    const excelDownloadURL = getExcelDownloadURL("newLostTimeseriesExcel", params);

    const { data = [], isLoading } = useBacklinksTimeseriesNewLostApi({
        key: params.key,
        from: START_DATE_13_M.format(API_DATE_REQUEST_FORMAT),
        to: TODAY.format(API_DATE_REQUEST_FORMAT),
        isDaily: true,
    });

    // State to manage the selected granularity and range
    const [granularityIndex, setGranularityIndex] = useState<number>(GRANULARITY_INDEX.MONTHLY);
    const [rangeIndex, setRangeIndex] = useState<number>(TIME_RANGES_INDEX.MONTH_13);
    const [activeTab, setActiveTab] = useState<number>(NEW_VS_LOST_GRAPH_TABS.REFERRING_DOMAINS);

    const metrics = [
        {
            metric: translate("digitalsuite.backlinks.overview.refdomains_count.title"),
        },
        {
            metric: translate("digitalsuite.backlinks.overview.backlinks_count.title"),
        },
    ];

    useEffect(() => {
        if (!currentGranularityAllowedInRange({ granularityIndex, rangeIndex })) {
            // Weekly is the value that is enabled for all ranges
            setGranularityIndex(GRANULARITY_INDEX.WEEKLY);
        }
    }, [rangeIndex, granularityIndex]);

    const availableChartTimeGranularityOptions = useMemo(() => {
        return getGranularityOptionsWithState({ currentRangeIndex: rangeIndex });
    }, [rangeIndex]);

    // Update granularity index
    // Update granularity index
    const granularityUpdate = (newGranularityIndex: number) => {
        allTrackers.dispatchTrackEvent(
            "Time Granularity",
            "Click",
            [
                "New vs Lost",
                `${chartTimeGranularityOptions[newGranularityIndex].title}`,
                `${chartTimeRangeOptions[rangeIndex].title}`,
            ].join("/"),
        );
        setGranularityIndex(newGranularityIndex);
    };

    // Update range index
    const rangeUpdate = (newRangeIndex: number) => {
        allTrackers.dispatchTrackEvent(
            "Time Frame Button",
            "Click",
            [
                "New vs Lost",
                `${chartTimeRangeOptions[newRangeIndex].title}`,
                `${chartTimeGranularityOptions[granularityIndex].title}`,
            ].join("/"),
        );
        setRangeIndex(newRangeIndex);
    };

    // Update active tab
    const onTabSelected = (index: number) => {
        allTrackers.dispatchTrackEvent("Metric Button", "Click", ["New vs Lost", metrics[index].metric].join("/"));
        setActiveTab(index);
    };

    // Filter data based on the selected range
    const rangeFilteredData = useMemo(() => {
        return filterDataFromDate(rangeIndex, data);
    }, [data, rangeIndex]);

    const getNewLostKeys = useCallback((): {
        newField: keyof Pick<BacklinksTimeSeriesNewLostSummary, "NewBacklinks" | "NewReferringDomains">;
        lostField: keyof Pick<BacklinksTimeSeriesNewLostSummary, "LostBacklinks" | "LostReferringDomains">;
    } => {
        if (activeTab === NEW_VS_LOST_GRAPH_TABS.REFERRING_DOMAINS) {
            return {
                newField: "NewReferringDomains",
                lostField: "LostReferringDomains",
            };
        }

        return {
            newField: "NewBacklinks",
            lostField: "LostBacklinks",
        };
    }, [activeTab]);

    // Aggregate (Sum up) all the data points for the same date by granularity
    // Monthly: Sum up all the data points for the same month
    // Weekly: Sum up all the data points for the same week
    // Daily: Sum up all the data points for the same day
    const granularityFilteredData = useMemo(() => {
        const { lostField, newField } = getNewLostKeys();
        return filterAggregateDataByGranularity({
            granularityIndex,
            data: rangeFilteredData,
            newField,
            lostField,
            rangeIndex,
        });
    }, [getNewLostKeys, granularityIndex, rangeFilteredData, rangeIndex]);

    // Format the filtered data for the chart
    const filteredReportData = useMemo(() => {
        if (!granularityFilteredData?.length) {
            return [];
        }

        const { lostField, newField } = getNewLostKeys();

        const formattedData = granularityFilteredData.reduce(
            (acc, item) => {
                const date = dayjs.utc(item.Date).valueOf();

                // Validate numbers
                const newValue = Number(item[newField]);
                const lostValue = Number(item[lostField]);

                acc.dataPositive.push([date, isNaN(newValue) ? 0 : newValue]);

                acc.dataNegative.push([date, isNaN(lostValue) ? 0 : -lostValue]);

                return acc;
            },
            { dataPositive: [], dataNegative: [] } as {
                dataPositive: number[][];
                dataNegative: number[][];
            },
        );

        return [
            {
                color: BacklinksChartColors.NEW_LOST_NEW,
                name: "New",
                data: formattedData.dataPositive,
                visible: true,
                type: "column",
            },
            {
                color: BacklinksChartColors.NEW_LOST_LOST,
                name: "Lost",
                data: formattedData.dataNegative,
                visible: true,
                type: "column",
            },
        ];
    }, [getNewLostKeys, granularityFilteredData]);

    const handleExcelClick = () => {
        allTrackers.dispatchTrackEvent(
            "search.backlinks.overview.new_lost_chart.excel.click",
            "click",
            "search.backlinks.overview.new_lost_chart.excel.click",
        );
    };

    const graphConfig = useMemo(() => {
        const config = getNewLostChartConfig(availableChartTimeGranularityOptions[granularityIndex].value);
        if (!Array.isArray(config.xAxis) && config.xAxis) {
            const firstDataPoint = filteredReportData?.[0]?.data?.[0];
            config.xAxis.min = Array.isArray(firstDataPoint) ? firstDataPoint[0] : null;
            const lastDataPoint = filteredReportData?.[0]?.data?.[filteredReportData?.[0]?.data?.length - 1];
            config.xAxis.max = Array.isArray(lastDataPoint) ? lastDataPoint[0] : null;
        }

        return config;
    }, [availableChartTimeGranularityOptions, granularityIndex, filteredReportData]);

    return (
        <BacklinksChartBoxContainer data-automation="new-vs-lost-chart">
            <WidgetContainer
                isLoading={isLoading}
                loaderWidth="100%"
                title={translate("digitalsuite.backlinks.overview.new_lost_chart.title")}
                tooltip={translate("digitalsuite.backlinks.overview.new_lost_chart.tooltip")}
                dynamicSwitcherConfig={[
                    {
                        items: chartTimeRangeOptions,
                        onItemClick: rangeUpdate,
                        initialSelectedItem: rangeIndex,
                    },
                    {
                        items: availableChartTimeGranularityOptions,
                        onItemClick: granularityUpdate,
                        initialSelectedItem: granularityIndex,
                    },
                ]}
                excelUrl={excelDownloadURL}
                onExcelClick={handleExcelClick}
                withBorder={false}
                downloadProps={{
                    showToast: () => dispatch(showErrorToast(translate("global.download.excel.error"))),
                }}
            >
                <Tabs
                    selectedIndex={activeTab}
                    onSelect={onTabSelected}
                >
                    <TabList>
                        {metrics?.map((metric) => (
                            <StyledTab
                                key={metric.metric}
                                data-automation={`tab-${metric.metric.replace(/\s+/g, "-").toLowerCase()}`}
                            >
                                {metric.metric}
                            </StyledTab>
                        ))}
                    </TabList>
                </Tabs>
                <BacklinksChartContainer>
                    <ColumnChartGraph
                        data={filteredReportData}
                        graphConfig={graphConfig}
                        shouldPresentLegend={false}
                    />
                </BacklinksChartContainer>
            </WidgetContainer>
        </BacklinksChartBoxContainer>
    );
};
